import React from 'react';
import { FileText, Sparkles } from 'lucide-react';
import type { FileUpload as FileUploadType, PromptTemplate, GlobalPromptTemplate } from '../types';
import { theme } from '../styles/theme';

interface PromptConfigTabProps {
  uploadedFiles: FileUploadType[];
  selectedFile: FileUploadType | null;
  setSelectedFile: (file: FileUploadType | null) => void;
  templates: PromptTemplate[];
  globalTemplates: GlobalPromptTemplate[];
  selectedTemplate: PromptTemplate | GlobalPromptTemplate | null;
  handleSelectTemplate: (template: PromptTemplate | GlobalPromptTemplate) => void;
  setSelectedTemplate: (template: PromptTemplate | GlobalPromptTemplate | null) => void;
  projectName: string;
  setProjectName: (name: string) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  onNextStep: () => void;
}

const PromptConfigTab: React.FC<PromptConfigTabProps> = ({
  uploadedFiles,
  selectedFile,
  setSelectedFile,
  templates,
  globalTemplates,
  selectedTemplate,
  handleSelectTemplate,
  setSelectedTemplate,
  projectName,
  setProjectName,
  prompt,
  setPrompt,
  onNextStep,
}) => {
  const [showValidation, setShowValidation] = React.useState(false);
  
  // Check if required fields are filled
  const isReadyForNextStep = selectedFile !== null && prompt.trim().length > 0;

  const handleNextStep = () => {
    if (!isReadyForNextStep) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    onNextStep();
  };

  // Reset validation when user fills in the fields
  React.useEffect(() => {
    if (isReadyForNextStep) {
      setShowValidation(false);
    }
  }, [isReadyForNextStep]);
  return (
    <div className={`${theme.card.background} rounded-lg shadow-lg p-6 mb-6`}>
      <h2 className={`text-xl font-semibold ${theme.text.primary} flex items-center mb-4`}>
        <Sparkles className="w-6 h-6 mr-2 text-text-secondary" />
        AI Prompt Configuration
      </h2>

      <div className="space-y-4">
        {/* File Selector */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Select File to Process <span className="text-qc-error">*</span>
          </label>
          <select
            value={selectedFile?.id || ''}
            onChange={(e) => {
              const file = uploadedFiles.find(f => f.id === e.target.value);
              setSelectedFile(file || null);
            }}
            className={`w-full px-4 py-2 border rounded-lg bg-bg-surface ${theme.text.primary} ${
              showValidation && !selectedFile
                ? 'border-qc-error ring-2 ring-qc-error'
                : 'border-border'
            }`}
          >
            <option value="">-- Select a file --</option>
            {uploadedFiles.map(file => (
              <option key={file.id} value={file.id}>
                {file.original_filename} ({file.row_count} rows, {file.column_count} columns)
              </option>
            ))}
          </select>
          {showValidation && !selectedFile && (
            <p className="text-sm text-qc-error mt-1">
               Please select a file to process
            </p>
          )}
        </div>

        {/* Template Selector */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
            Use Saved Template (Optional)
          </label>
          <div className="flex space-x-2">
            <select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const userTemplate = templates.find(t => t.id === value);
                const globalTemplate = globalTemplates.find(t => t.id === value);
                const template = userTemplate || globalTemplate;
                
                if (template) {
                  handleSelectTemplate(template);
                }
              }}
              className={`flex-1 px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
            >
              <option value="">-- No template --</option>
              
              {/* Global Templates */}
              {globalTemplates.length > 0 && (
                <optgroup label="🌐 Global Templates">
                  {globalTemplates.map(template => (
                    <option key={`global-${template.id}`} value={template.id}>
                      {template.name} ({template.category})
                    </option>
                  ))}
                </optgroup>
              )}
              
              {/* User Templates */}
              {templates.length > 0 && (
                <optgroup label="👤 My Templates">
                  {templates.map(template => (
                    <option key={`user-${template.id}`} value={template.id}>
                      {template.is_favorite && ' '}{template.name} {template.is_default && '(Default)'}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {selectedTemplate && (
              <button
                onClick={() => {
                  setSelectedTemplate(null);
                  setPrompt('');
                }}
                className={`px-4 py-2 bg-bg-hover ${theme.text.secondary} rounded-lg hover:bg-bg-hover`}
              >
                Clear
              </button>
            )}
          </div>
          {selectedTemplate && (
            <div className={`mt-2 p-3 ${theme.status.info.background} border border-interactive-primary/30 rounded-lg`}>
              {'category' in selectedTemplate && (
                <p className="text-xs font-medium text-interactive-primary mb-1">
                  🌐 Global Template • {selectedTemplate.category}
                </p>
              )}
              <p className={`text-sm ${theme.text.secondary}`}>
                {selectedTemplate.description}
              </p>
            </div>
          )}
        </div>

        {/* Project Name Input */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2 flex items-center`}>
            <FileText className="w-4 h-4 mr-2" />
            Project Name (Optional)
          </label>
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="e.g., Q4 Analysis, Customer Data Review..."
            className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-interactive-primary focus:border-transparent`}
          />
          <p className="text-xs text-text-tertiary mt-1">
            📁 Output filename format: ProjectName_FileName_YYYY-MM-DD_HHMM.csv
          </p>
        </div>

        {/* Prompt Input */}
        <div>
          <label className={`block text-sm font-medium ${theme.text.secondary} mb-2 flex items-center`}>
            <FileText className="w-4 h-4 mr-2" />
            Processing Prompt <span className="text-qc-error ml-1">*</span>
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Enter your AI processing instructions here... e.g., 'Analyze each row and extract key information about the product including category, specifications, and pricing details.'"
            rows={6}
            className={`w-full px-4 py-3 border rounded-lg bg-bg-surface ${theme.text.primary} resize-none focus:ring-2 focus:ring-interactive-primary focus:border-transparent ${
              showValidation && prompt.trim().length === 0
                ? 'border-qc-error ring-2 ring-qc-error'
                : 'border-border'
            }`}
          />
          {showValidation && prompt.trim().length === 0 && (
            <p className="text-sm text-qc-error mt-1">
               Please enter a processing prompt
            </p>
          )}
          <p className="text-sm text-text-tertiary mt-2">
            Character count: {prompt.length} | Estimated tokens: ~{Math.ceil(prompt.length / 4)}
          </p>
        </div>

        {/* Row & Token Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
              Expected Rows to Process
            </label>
            <input
              type="number"
              value={selectedFile?.row_count || 0}
              disabled
              className={`w-full px-4 py-2 border border-border rounded-lg ${theme.background.tertiary} text-text-primary cursor-not-allowed`}
            />
            {!selectedFile && (
              <p className="text-sm text-qc-error mt-1">
                Please select a file first
              </p>
            )}
          </div>
        </div>

        {/* Next Step Button */}
        <div className="flex justify-end mt-6 pt-4 border-t border-border">
          <button
            onClick={handleNextStep}
            className={`px-6 py-3 font-semibold rounded-lg transition duration-200 ${
              isReadyForNextStep
                ? 'bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse'
                : 'bg-bg-hover text-text-secondary cursor-pointer'
            }`}
          >
            Next Step: Configure Processing →
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptConfigTab;
